import { Badge } from "@/components/Badge";
import { DownloadButton } from "@/components/DownloadButton";
import { ExportStatus, EXPORT_VALID_STATUSES } from "@/lib/utils/exportStatus";
import { statusToBadgeColor } from "@/lib/utils/statusTobadgeColor";
import { parseDate, parseDateWithHours } from "@/lib/utils";
import { ColumnDef } from "@tanstack/react-table";
import type { TFunction } from "i18next";
import { createEnum } from "@/lib/utils/createEnum";
import { Clients } from "@/lib/clients";

export type TDownloadCenter = {
  id: string;
  created_at?: string;
  client: string;
  file_name: string;
  status: string;
  reporting_date?: string;
  user_name?: string;
};
export const c5DownloadCenterFields = createEnum<TDownloadCenter>();

export const getDownloadCenterColumns = (
  t: TFunction,
): ColumnDef<TDownloadCenter>[] => [
  {
    accessorKey: c5DownloadCenterFields.id,
    header: t("technical_info.id", { defaultValue: "ID" }),
  },
  {
    accessorKey: c5DownloadCenterFields.client,
    header: t("download_center.columns.client", {
      defaultValue: "Client",
    }),
  },
  {
    accessorKey: c5DownloadCenterFields.reporting_date,
    header: t("download_center.columns.reporting_date", {
      defaultValue: "Reporting date",
    }),
    cell: ({ row }) => {
      const cell_data: string | undefined = row.getValue(
        c5DownloadCenterFields.reporting_date,
      );
      if (cell_data) {
        return parseDate(cell_data);
      }
      return "";
    },
  },
  {
    accessorKey: c5DownloadCenterFields.status,
    header: t("download_center.columns.status", {
      defaultValue: "Status",
    }),
    cell: ({ getValue }) => {
      const value = getValue() as ExportStatus;
      const badgeColor = statusToBadgeColor[value] || "error";
      return <Badge color={badgeColor} />;
    },
  },
  {
    accessorKey: c5DownloadCenterFields.file_name,
    header: t("download_center.columns.file_name", {
      defaultValue: "File name",
    }),
    cell: ({ row, getValue }) => {
      const status = row.getValue(c5DownloadCenterFields.status) as ExportStatus;
      const value = getValue() as string;

      if (EXPORT_VALID_STATUSES[status]) {
        return (
          <span className="whitespace-nowrap overflow-hidden text-ellipsis block">
            {value}
          </span>
        );
      } else {
        return null;
      }
    },
  },
  {
    accessorKey: c5DownloadCenterFields.user_name,
    header: t("download_center.columns.user_name", {
      defaultValue: "User",
    }),
  },
  {
    accessorKey: c5DownloadCenterFields.created_at,
    header: t("download_center.columns.created_at", {
      defaultValue: "Generated at",
    }),
    cell: ({ row }) => {
      // Display creation date even if the status is pending
      const cell_data: string | undefined = row.getValue(
        c5DownloadCenterFields.created_at,
      );
      if (cell_data) {
        return parseDateWithHours(cell_data);
      }
      return "";
    },
  },
  {
    accessorKey: "download",
    header: t("actions.download", { defaultValue: "Download" }),
    cell: ({ row }) => {
      const status = row.getValue(c5DownloadCenterFields.status) as ExportStatus;
      const id = row.getValue(c5DownloadCenterFields.id) as string;
      const fileName = row.getValue(c5DownloadCenterFields.file_name) as string;
      const href = `${
        import.meta.env.VITE_BACKEND_URL
      }/${Clients.C5}/c5_download_center/download/${id}`;

      if (EXPORT_VALID_STATUSES[status]) {
        return (
          <DownloadButton
            href={href}
            filename={fileName}
            size="sm"
            variant="light"
            color="primary"
            isIconOnly
          />
        );
      } else {
        return null;
      }
    },
  },
];
